import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axios';

const API_URL = '/api/cars';

export const fetchCars = createAsyncThunk('cars/fetchCars', async (params = {}, { getState, rejectWithValue }) => {
  try {
    const { pagination } = getState().cars;
    const queryParams = {
      page: pagination.page,
      limit: pagination.limit,
      ...params,
    };
    const response = await api.get(API_URL, { params: queryParams });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchCarById = createAsyncThunk('cars/fetchCarById', async (id, { rejectWithValue }) => {
  try {
    const response = await api.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const createCar = createAsyncThunk('cars/createCar', async (carData, { rejectWithValue }) => {
  try {
    const response = await api.post(API_URL, carData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const updateCar = createAsyncThunk('cars/updateCar', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(`${API_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const deleteCar = createAsyncThunk('cars/deleteCar', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`${API_URL}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// ✅ НОВИЙ THUNK для повернення з архіву
export const restoreCar = createAsyncThunk('cars/restoreCar', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(`${API_URL}/${id}/restore`, data);
    return response.data.car;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const exportCarsToCSV = createAsyncThunk(
  'cars/exportToCSV',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { filters } = getState().cars;
      const response = await api.get(`${API_URL}/export/csv`, {
        params: {
          status: filters.status,
          search: filters.search,
          city: filters.city,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cars_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка експорту');
    }
  }
);

export const uploadImages = createAsyncThunk('cars/uploadImages', async (formData, { rejectWithValue }) => {
  try {
    const response = await api.post('/api/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.images;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const initialState = {
  cars: [],
  // Aggregates computed by the server over the WHOLE selection (not just the
  // current page). Present only when the request asked for withSummary=1.
  summary: null,
  currentCar: null,
  loading: false,
  uploading: false,
  error: null,
  filters: {
    status: '',
    search: '',
    brand: '',
    city: '',
    imported_from: '',
    price_from: '',
    price_to: ''
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }
};

const carsSlice = createSlice({
  name: 'cars',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    clearCurrentCar: (state) => {
      state.currentCar = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCars.pending, (state) => { 
        state.loading = true; 
      })
      .addCase(fetchCars.fulfilled, (state, action) => {
        state.loading = false;
        // T-13: Parse photos once in the reducer instead of inline during every render
        state.cars = (action.payload.cars || []).map(car => ({
          ...car,
          photos: typeof car.photos === 'string' ? (() => { try { return JSON.parse(car.photos); } catch { return []; } })() : (car.photos || [])
        }));
        state.pagination = action.payload.pagination;
        state.summary = action.payload.summary || null;
      })
      .addCase(fetchCars.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Помилка';
      })
      .addCase(fetchCarById.fulfilled, (state, action) => {
        state.currentCar = action.payload;
      })
      .addCase(createCar.fulfilled, (state, action) => {
        state.cars.unshift(action.payload);
      })
      .addCase(updateCar.fulfilled, (state, action) => {
        const index = state.cars.findIndex(car => car.id === action.payload.car.id);
        if (index !== -1) {
          state.cars[index] = action.payload.car;
        }
      })
      .addCase(deleteCar.fulfilled, (state, action) => {
        state.cars = state.cars.filter(car => car.id !== action.payload);
      })
      // ✅ НОВА ОБРОБКА для повернення з архіву
      .addCase(restoreCar.fulfilled, (state, action) => {
        const restoredCar = action.payload;
        const index = state.cars.findIndex(car => car.id === restoredCar.id);
        
        if (index !== -1) {
          // Авто вже є в списку - оновлюємо
          state.cars[index] = restoredCar;
        } else {
          // Авто немає в списку - додаємо на початок
          state.cars.unshift(restoredCar);
        }
      })
      .addCase(uploadImages.pending, (state) => { 
        state.uploading = true; 
      })
      .addCase(uploadImages.fulfilled, (state) => { 
        state.uploading = false; 
      })
      .addCase(uploadImages.rejected, (state) => { 
        state.uploading = false; 
      });
  },
});

export const { setFilters, setPage, clearCurrentCar } = carsSlice.actions;
export default carsSlice.reducer;
